import {
  Stack,
  Checkbox,
  Group,
  TextInput,
  Textarea,
  NumberInput,
} from "@mantine/core"
import { useForm } from "@mantine/form"
import { CancelButton, CreateButton } from "../../../../-components/Buttons"
import { AddCalendarProfile } from "../../../../@client/Theasoft_ApiServer_WebCalendar"
import { ErrorAlert } from "../../../../blocks/errors/error-alert"
import { useSuspenseQueryGet } from "../../../../typed-client/hooks/use-suspense-query-get"
import { pathProxy } from "../../../../utils/forms-proxy"
import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { usePostMutation } from "../../../../typed-client/hooks/use-post-mutation"
import { DefaultModal } from "../../../../-components/default-modal"

interface ModalProps {
  addCalendarProfilePathProxy: ReturnType<typeof pathProxy<AddCalendarProfile>>
  opened: boolean
  onClose: () => void
}

export function CreateCalendarModal({
  onClose,
  opened,
  addCalendarProfilePathProxy,
}: ModalProps) {
  const { t } = useTranslation("app_server")
  const client = useQueryClient()

  const defaults = useSuspenseQueryGet(
    "/api/web-calendar/add-personal-web-calendar-profile-defaults",
    null
  )
  const form = useForm<AddCalendarProfile>({
    initialValues: defaults.data,
  })
  const addProfile = usePostMutation(
    "/api/web-calendar/AddPersonalWebCalendarProfile",
    {
      onSuccess: () => {
        onClose()
        client.invalidateQueries({
          queryKey: ["/api/web-calendar/get-profiles", null],
        })
      },
    }
  )
  return (
    <DefaultModal
      size="lg"
      centered
      title={t("Calendar_Subscriptions_CreateButton")}
      onClose={onClose}
      opened={opened}
    >
      <form onSubmit={form.onSubmit((values) => addProfile.mutate(values))}>
        <Stack gap="xs">
          <Checkbox.Group
            label={t("AppointmentTypes")}
            description={t(
              "Calendar_Subscriptions_AppointmentTypesDescription"
            )}
          >
            {/* empty CheckboxGroup just for the label and description, else it would control the state (we want each checkbox to control a property) */}
            <div />
          </Checkbox.Group>
          <Group>
            <Checkbox
              label={t("Absences")}
              {...form.getInputProps(
                addCalendarProfilePathProxy.settings.includeAbsenceAppointments
                  ._PATH_,
                { type: "checkbox" }
              )}
            />
            <Checkbox
              label={t("Casts")}
              {...form.getInputProps(
                addCalendarProfilePathProxy.settings.includeCastAppointments
                  ._PATH_,
                { type: "checkbox" }
              )}
            />
            <Checkbox
              label={t("DutyRosters")}
              {...form.getInputProps(
                addCalendarProfilePathProxy.settings
                  .includeDutyRosterAppointments._PATH_,
                { type: "checkbox" }
              )}
            />
            <Checkbox
              label={t("Travels")}
              {...form.getInputProps(
                addCalendarProfilePathProxy.settings.includeTravelAppointments
                  ._PATH_,
                { type: "checkbox" }
              )}
            />
          </Group>

          <TextInput
            autoFocus
            label={t("Name")}
            data-autofocus
            placeholder={t("Calendar_Subscriptions_CreateModalNamePlaceholder")}
            {...form.getInputProps(
              addCalendarProfilePathProxy.profile.name._PATH_
            )}
          />
          <Textarea
            label={t("Description")}
            placeholder={t(
              "Calendar_Subscriptions_CreateModalDescriptionPlaceholder"
            )}
            {...form.getInputProps(
              addCalendarProfilePathProxy.profile.description._PATH_
            )}
          />
          <Group>
            <NumberInput
              label={t("NumberOfDaysInThePast")}
              {...form.getInputProps(
                addCalendarProfilePathProxy.settings.daysOffsetInPast._PATH_
              )}
            />
            <NumberInput
              label={t("NumberOfDaysInTheFuture")}
              {...form.getInputProps(
                addCalendarProfilePathProxy.settings.daysOffsetInFuture._PATH_
              )}
            />
          </Group>
          <ErrorAlert error={addProfile.error} />
          <Group mt="md">
            <CancelButton onClick={onClose} />
            <CreateButton loading={addProfile.isPending} />
          </Group>
        </Stack>
      </form>
    </DefaultModal>
  )
}
